import { useState } from 'react';
import { useEffect, useRef } from 'react';

import { supabase } from '../supabaseClient';
import { loadMapKit } from '../MapKitLoader';
import { JamPadEvent } from '../DataModels';

import MapSideScroller from './MapSideScroller.tsx';
import Search from '@mui/icons-material/Search';
import { UUID } from 'crypto';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, MenuItem, Slider, Switch, TextField, Typography } from '@mui/material';
import { EventMapContext } from '../App/App.tsx';
import { EventItem } from '../Events/EventItem.tsx';
import { ArrowBack, ArrowDownward, ArrowUpward, Close, Tune } from '@mui/icons-material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from "dayjs";
import { ages } from '../CreateEvent/CreateEvent.tsx';
import EventTagAdder from '../CreateEvent/EventTagAdder.tsx';
import { Tag } from '../Tags.ts';

// let mapkit be accessible (allow compiler to assume it is rendered properly)
declare global {
    interface Window { mapkit: any; }
}

export type MapFilter = {
    startDate: Dayjs, 
    endDate: Dayjs,
    maxCost: number, ages: number, verified: boolean,
    tags: Tag[], eventLimit: number
}

export default function AppleMap() {
    const defaultEventLimit = 30;
    const defaultDaysForward = 30;
    const mapRef = useRef(null);
    const mapInstance: any = useRef(null);

    const [eventMap, setEvents] = useState<Map<UUID, JamPadEvent>>(new Map<UUID, JamPadEvent>());
    const localEventMapRef = useRef(eventMap);
    const [selectedID, setID] = useState<UUID>();
    const [showScroller, setShowScroller] = useState<Boolean>(false);

    const [clusterMode, setClusterMode] = useState("none");
    const [clusterMap, setCluster] = useState<Map<UUID, JamPadEvent>>(new Map<UUID, JamPadEvent>());

    const [filterPopup, setFilterPopup] = useState(false);
    const [filtersActive, setFiltersActive] = useState(false);
    const [filters, setFilters] = useState({
        startDate: dayjs(Date.now()), endDate: dayjs(Date.now() + (1000 * 60 * 60 * 24 * defaultDaysForward)),
        maxCost: 20, ages: 21, verified: true,
        tags: Array<Tag>(), eventLimit: defaultEventLimit
    });

    function loadClusterSetting() {
        const cached = localStorage.getItem("clusterMode");
        if(cached && (cached === "none" || cached === "venue" || cached === "all")) {
            setClusterMode(cached);
        }
    }

    // maintains up to date state of map for reading properly
    useEffect(() => {
        localEventMapRef.current = eventMap;
    }, [eventMap]);

    async function loadMapEvents(minLng: number, maxLng: number, minLat: number, maxLat: number) {
        if(!filtersActive) {
            const { data: events, error } = await supabase
                .rpc('load_map_feed', { 
                    min_lng: minLng, max_lng: maxLng, 
                    min_lat: minLat, max_lat: maxLat,
                    limit_count: defaultEventLimit
                })
                .select().overrideTypes<JamPadEvent[]>()
            if(error) { 
                console.error(error.message) 
                return null
            } else {
                return events
            }
        } else {
            const { data: events, error } = await supabase
                .rpc('load_map_feed', {
                    min_lng: minLng, max_lng: maxLng, min_lat: minLat, max_lat: maxLat,
                    before_date: filters.endDate, after_date: filters.startDate,
                    max_age: filters.ages, max_price: filters.maxCost, 
                    tags_filter: filters.tags, verified_filter: filters.verified, 
                    limit_count: filters.eventLimit
                })
                .select().overrideTypes<JamPadEvent[]>()
            if(error) { 
                console.error(error.message) 
                return null
            } else {
                return events
            }
        }
    };

    useEffect(() => {
        loadMapKit().then( (mapkit) => {
            if (!mapInstance.current && mapRef.current) {
                var center = new mapkit.Coordinate(39.9625, -83.0032); // fallback
                var span = new mapkit.CoordinateSpan(1.0, 1.0); // fallback

                const savedState = JSON.parse(localStorage.getItem("mapSpan") || "null");
                if (savedState) {
                    span = new mapkit.CoordinateSpan(savedState.spanLat ?? 1.0, savedState.spanLng ?? 1.0)
                }

                mapInstance.current = new mapkit.Map(mapRef.current, {
                    region: new mapkit.CoordinateRegion(center, span),
                    showsUserLocation: true, tracksUserLocation: true, showsUserLocationControl: true
                });
                if(mapInstance.current !== null) {
                    mapInstance.current.addEventListener("region-change-end", () => {
                        const span = mapInstance.current.region.span;
                        const state = { spanLat: span.latitudeDelta, spanLng: span.longitudeDelta };
                        localStorage.setItem("mapSpan", JSON.stringify(state));
                    });
                    mapInstance.current.addEventListener("select", (e) => {
                        handleMapClick(e);
                    });

                    // map errors
                    mapInstance.current.addEventListener("error", function(e) {
                        console.error(e);
                    });
                }
            }
        });

        // load cluster setting
        loadClusterSetting();
    
        return () => {
            if (mapInstance.current) {
                mapInstance.current.destroy();
                mapInstance.current = null;
            }
        };
    }, []);

    const handleMapClick = (e) => {
        if(e.annotation.memberAnnotations && e.annotation.memberAnnotations.length > 0) {
            const clusterMap = new Map<UUID, JamPadEvent>();
            for(let a of e.annotation.memberAnnotations){
                console.log(a.id);
                var temp = localEventMapRef.current.get(a.id as UUID);
                if(temp) {
                    var s = clusterMap.set(a.id, temp);
                }
            }
            setCluster(clusterMap);
            setShowScroller(true);
        } else {
            setID(e.annotation.id);
            setShowScroller(true);
        }
    }

    const doEventSearch = async () => { 
        const lat = mapInstance.current.center.latitude;
        const lng = mapInstance.current.center.longitude;
        // multiply to larger than screen to search a fuller 
        const latSpan = (mapInstance.current.region.span.latitudeDelta / 2.0) * 1.2;
        const lngSpan = (mapInstance.current.region.span.longitudeDelta / 2.0) * 1.2;

        const events = await loadMapEvents(lng - lngSpan, lng + lngSpan, lat - latSpan, lat + latSpan);
        if(events) {
            loadMapKit().then( (mapkit) => {
                var createdAnnotations: any[] = [];
                var newMap = new Map<UUID, JamPadEvent>();
                events.forEach( (e) => {
                    e.date = new Date(e.date);
                    const coord = new mapkit.Coordinate(e.lat, e.lng);
                    const marker = new mapkit.MarkerAnnotation(coord, {
                        color: 'darkblue',
                        glyphImage: { 1: "frog_head.png" },
                        collisionMode: clusterMode === 'none' ? 'none' : 'circle',
                        clusteringIdentifier: clusterMode === "venue" ? e.venue_id : "all",
                        title: e.date.toLocaleString('en-US', {month: 'short', day: 'numeric'}),
                        id: e.id,
                    });
                    createdAnnotations.push(marker);
                    newMap.set(e.id, e);
                });
                setEvents(newMap);
                mapInstance.current.annotations = createdAnnotations;
            })
        }
    };

    useEffect(() => {
        loadMapKit().then((mapkit) => {
            const newAnnotations: any[] = [];
            Array.from(eventMap.values()).forEach( (e) => {
                const coord = new mapkit.Coordinate(e.lat, e.lng);
                const marker = new mapkit.MarkerAnnotation(coord, {
                    color: 'darkblue',
                    glyphImage: { 1: "frog_head.png" },
                    collisionMode: clusterMode === 'none' ? 'none' : 'circle',
                    clusteringIdentifier: clusterMode === "venue" ? e.venue_id : "all",
                    title: e.date.toLocaleString('en-US', {month: 'short', day: 'numeric'}),
                    id: e.id,
                });
                newAnnotations.push(marker);
            });
            mapInstance.current.annotations = newAnnotations;
        })
    }, [clusterMode])

    const toggleScroller = () => {
        setShowScroller(!showScroller);
    };

    return <>
        <Box component='div' sx={{ flex: '1', display: { 
                xs: selectedID === undefined ? 'flex' : 'none', md: 'flex' 
            }, overflow: 'hidden', position: 'relative',
            flexDirection: { xs: 'column-reverse', md: 'row' }, padding: '0'
        }}>
            { showScroller && ( clusterMap.size > 0 ?
                <EventMapContext.Provider value={{ events: clusterMap, setEvents: (m) => { setCluster(m) }}}>
                    <MapSideScroller eventMap={clusterMap} fullEventID={selectedID} 
                        cluster={true} setCluster={(c) => setCluster(c)}
                        backToFull={() => { setID(undefined) }} seeFullEvent={(id) => { setID(id) }}/>
                </EventMapContext.Provider>
            :
                <EventMapContext.Provider value={{ events: eventMap, setEvents: (m) => { setEvents(m) }}}>
                    <MapSideScroller eventMap={eventMap} fullEventID={selectedID} 
                        cluster={false} setCluster={() => console.log("set cluster called in non-cluster scroller...")}
                        backToFull={() => { setID(undefined) }} seeFullEvent={(id) => { setID(id) }}/>
                </EventMapContext.Provider>
            )}

            <div style={{ flex: '1', display: 'flex', position: 'relative', zIndex: '3' }}>
                <Box sx={{ zIndex: '2', position: 'absolute', bottom: '5px', 
                    right: { xs: 'calc(50vw - 80px)', md: showScroller ? 'calc(25vw - 76px)' : 'calc(50vw - 76px)'}
                }}>
                    <Box onClick={doEventSearch} sx={{ 
                        background: 'darkblue', color: 'white', 
                        borderRadius: '60px', 
                        padding: { xs: '20px 52px', md: '20px 60px' }
                    }}>
                        <Search fontSize='large'/>
                    </Box>
                </Box>
                <div style={{ zIndex: '2', position: 'absolute', 
                    top: '10px', left: '10px', display: 'flex', gap: '10px'
                }}>
                    <Button variant='contained' sx={{ 
                        backgroundColor: filtersActive ? 'lightblue' : 'lightgray', 
                        color: filtersActive ? 'darkblue' : 'gray',
                        borderRadius: '40px', margin: '0', padding: '0'
                    }} onClick={() => {
                        setFilterPopup(!filterPopup);
                    }}><Tune/></Button>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        background: 'lightblue', borderRadius: '12px', padding: '8px', gap: '4px'
                     }}>
                        <Typography variant='caption' fontWeight={'bold'}>Marker Clustering Mode</Typography>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            { ['all', 'venue', 'none'].map((option) => {
                                return <Button key={option}
                                    sx={{ fontSize: '10px', padding: '2px', borderRadius: '10px' }} 
                                    variant={ clusterMode === option ? 'contained' : 'text'}
                                    onClick={() => {
                                        setClusterMode(option);
                                        localStorage.setItem("clusterMode", option);
                                    }}
                                >{option}</Button>
                            })}
                        </div>
                    </div>
                </div>
                <Box sx={{ zIndex: '3', position: 'absolute', 
                    top: { xs: 'unset', md: '45%' }, bottom: { xs: '-28px' },
                    left: { xs: '20%', md: '-30px' }
                }}>
                    <Button onClick={toggleScroller} sx={{
                        padding: '10px', background: 'lightblue', color: 'black', borderRadius: '100px',
                        display: { xs: 'none', md: 'flex' }, border: '4px solid darkblue'
                    }}>
                        { showScroller ?  <ArrowBackIos style={{margin: '2px 0 2px 8px'}} fontSize='large' /> 
                            : <ArrowForwardIos style={{margin: '4px 4px 4px 18px'}} fontSize='large' /> }
                    </Button>

                    <Button onClick={toggleScroller} sx={{
                        padding: '8px', background: 'lightblue', color: 'black', borderRadius: '100px',
                        display: { xs: 'flex', md: 'none' }, border: '3px solid darkblue'
                    }}>
                        { showScroller ?  <ArrowDownward sx={{ margin: '6px 0' }} fontSize='medium' /> 
                            : <ArrowUpward sx={{ margin: '4px 0 20px' }}fontSize='medium' /> }
                    </Button>
                </Box>
                <div ref={mapRef} style={{ flex: '1', zIndex: '1' }}></div>
            </div> 
        </Box>



        {/* For smaller screens, overlay whole screen */}
        {selectedID !== undefined && (
            <Box sx={{ overflow: 'hidden', display: { xs: 'flex', md: 'none' }, 
                zIndex: 4, flex: '1', position: 'relative', background: 'darkgray'
            }}>
                <div style={{ overflow: 'auto', zIndex: '1'}}>
                    <EventItem col={false} event={eventMap.get(selectedID)} />
                </div>
                <div style={{ zIndex: '2', position: 'absolute', top: '10px', left: '10px'}}>
                    <Button onClick={ () => setID(undefined) } sx={{
                        borderRadius: '32px', padding: '10px 0'
                    }} variant='contained'>
                        <ArrowBack /></Button>
                </div>
            </Box>
        )}



        {/* Map filter overlay */}
        <Dialog open={filterPopup} onClose={() => setFilterPopup(false)}>
            <DialogTitle sx={{
                margin: '4px auto', padding: '14px 0 0'
            }}><b>Map Filters ({filtersActive ? "Active" : "Not Active"})</b></DialogTitle>
            <IconButton onClick={() => setFilterPopup(false)}
                sx={{ position: 'absolute',
                    right: 8, top: 8, color: 'darkblue',
            }}><Close/></IconButton>
            <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <FormControlLabel control={
                        <Switch checked={filtersActive}
                            onChange={(e) => setFiltersActive(e.target.checked)}
                            slotProps={{ input: { 'aria-label': 'controlled' } }}
                        />
                    } label="Activate Filters" />

                    <div style={{ display: 'flex', gap: '20px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker sx={{ flex: '1'}} label="Start Date"
                                minDate={dayjs(Date.now())}
                                value={filters.startDate} onChange={(d) => setFilters({
                                    ...filters, startDate: dayjs(d) 
                                })}
                            />
                            <DatePicker sx={{ flex: '1'}} label="End Date"
                                minDate={dayjs(filters.startDate)}
                                value={filters.endDate} onChange={(d) => setFilters({
                                    ...filters, endDate: dayjs(d)
                                })}
                            />
                        </LocalizationProvider>
                    </div>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <TextField style={{ flex: '1' }} label="Max Cost" type="number"
                            value={filters.maxCost} onChange={(e) => setFilters({ 
                                ...filters, maxCost: parseInt(e.target.value)
                            })}
                            slotProps={{ input: { 
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}}/>
                            
                        <TextField style={{ flex: '1' }} select label="Age Requirement" 
                            value={filters.ages} onChange={(e) => setFilters({
                                ...filters, ages: parseInt(e.target.value)
                            })}>
                            {ages.map((option) => (
                                <MenuItem key={option.val} value={option.val}>
                                    {option.label}{option.val != 0 ? " and under" : ""}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                    <EventTagAdder tags={filters.tags} 
                        removeTag={(t) => {
                            const temp = Array.from(filters.tags.filter((tag) => {
                                return tag !== t;
                            }).values());
                            setFilters({...filters, tags: temp});
                        }}
                        addTag={(t) => {
                            const temp = Array.from(filters.tags.values());
                            temp.push(t);
                            setFilters({...filters, tags: temp});
                        }}
                    />

                    <FormControlLabel sx={{ textWrap: 'nowrap' }} control={
                        <Switch checked={filters.verified}
                            onChange={(e) => setFilters({
                                ...filters, verified: e.target.checked
                            })}
                            slotProps={{ input: { 'aria-label': 'controlled' } }}
                        />
                    } label="Only Show Verified Events" />

                    <div style={{ display: 'flex', gap: '20px'}}>
                        <Typography sx={{ textWrap: 'nowrap' }}><b>Event Limit:</b> {filters.eventLimit}</Typography>
                        <Slider onChange={(_e, val) => setFilters({
                            ...filters, eventLimit: val
                        })}
                            aria-label="Max Events Searched"
                            defaultValue={defaultEventLimit}
                            valueLabelDisplay="auto"
                            step={1} min={1} max={100}
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ padding: '0 12px 18px' }}>
                <Button color='warning' onClick={() => setFilters({
                    startDate: dayjs(Date.now()), 
                    endDate: dayjs(Date.now() + (1000 * 60 * 60 * 24 * defaultDaysForward)),
                    maxCost: 20, ages: 21, verified: true,
                    tags: [], eventLimit: defaultEventLimit
                })}>Reset Filters</Button>

                <Button onClick={() => {
                    setFiltersActive(true);
                    setFilterPopup(false);
                    doEventSearch();
                }}>Apply Filters</Button>
            </DialogActions>
        </Dialog>
    </>;
}